import React, { useEffect, useState } from "react";
import { Play, Clock, Video as VideoIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function Training() {
  const { activeProgram } = useAuth();
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/videos", { params: { program: activeProgram } }).then((r) => setVideos(r.data)).catch(() => {});
  }, [activeProgram]);

  return (
    <div>
      <PageHeader
        eyebrow="The Library · Screen"
        title="Training Videos"
        subtitle="Role-scoped video guides with searchable transcripts. Watch, learn, get to work."
        testid="training-header" />

      {videos.length === 0 ? (
        <EmptyState icon={VideoIcon} title="No videos yet" hint="Your program manager hasn't added training videos here." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="training-grid">
          {videos.map((v) => (
            <button key={v.id} onClick={() => setActive(v)} data-testid={`video-card-${v.id}`}
              className="text-left kp-card kp-card-lift overflow-hidden group">
              <div className="aspect-video bg-primary relative flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-70" style={{ background: "radial-gradient(circle at 30% 30%, hsl(102 78% 91% / 0.35), transparent 55%), radial-gradient(circle at 70% 70%, hsl(208 63% 78% / 0.35), transparent 55%)" }} />
                <div className="w-14 h-14 rounded-full bg-background/90 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 relative z-10 shadow-xl">
                  <Play className="w-6 h-6 text-primary ml-0.5" fill="currentColor" />
                </div>
                <span className="absolute bottom-3 right-3 text-[11px] font-mono bg-foreground/70 text-background rounded-full px-2 py-0.5 flex items-center gap-1 backdrop-blur">
                  <Clock className="w-3 h-3" /> {v.duration}
                </span>
              </div>
              <div className="p-5">
                <p className="font-display text-lg text-foreground leading-tight">{v.title}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(v.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] font-mono uppercase tracking-wider rounded-full bg-secondary/50 text-primary px-2 py-0.5">#{t}</span>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{active?.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-primary rounded-xl flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 30%, hsl(102 78% 91% / 0.35), transparent 55%), radial-gradient(circle at 70% 70%, hsl(208 63% 78% / 0.35), transparent 55%)" }} />
            <div className="w-16 h-16 rounded-full bg-background/90 flex items-center justify-center relative z-10 shadow-xl">
              <Play className="w-7 h-7 text-primary ml-0.5" fill="currentColor" />
            </div>
          </div>
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground mb-1.5">Transcript</p>
            <p className="text-sm text-foreground/80 leading-relaxed font-editorial">{active?.transcript}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
